import { useState, useEffect } from 'react';
import { Button, Form, Row, Col } from 'react-bootstrap';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Chip from '@mui/material/Chip';

import { useRestApi } from 'context/RestApiContext';
import { MessageModal } from 'components/modal/MessageModal';
import SelectComponent from 'components/Input/SelectComponent';

const WEEKDAYS = ['月', '火', '水', '木', '金', '土', '日'];

function RecurringTaskManageModal(props) {
  const restApi = useRestApi();

  const [show, setShow] = useState(false);
  const [recurringCards, setRecurringCards] = useState([]);
  const [lists, setLists] = useState([]);
  const [dialog, setDialog] = useState({});

  // 追加モード
  const [addMode, setAddMode] = useState(false);
  const [newCard, setNewCard] = useState({});
  const [newChecklist, setNewChecklist] = useState([]);
  const [newItemText, setNewItemText] = useState('');

  useEffect(() => {
    if (props?.params?.show) {
      loadRecurringCards();
      setShow(true);
      setAddMode(false);
    }
  }, [props.params]);

  const loadRecurringCards = () => {
    restApi.get('/api/task/recurring-cards', (data) => {
      setRecurringCards(data.cards);
      setLists(data.lists);
    });
  };

  const handleClose = () => {
    setShow(false);
    setAddMode(false);
  };

  // 定期タスク付きカード追加
  const handleAddNew = () => {
    setNewCard({
      TITLE: '',
      DESCRIPTION: '',
      LIST_ID: lists.length > 0 ? lists[0].LIST_ID : '',
      FREQUENCY: 'weekly',
      DAY_OF_WEEK: 0,
      DAY_OF_MONTH: 1,
      END_DAY_OF_WEEK: null,
      END_DAY_OF_MONTH: null,
      END_TIME: '',
    });
    setNewChecklist([]);
    setAddMode(true);
  };

  // 編集ボタン → TaskCardEditModalを開く
  const handleEdit = (card) => {
    handleClose();
    if (props.callEditModal) {
      props.callEditModal({ CARD_ID: card.CARD_ID });
    }
  };

  // 有効/無効切り替え
  const handleToggleEnabled = (card) => {
    const newEnabled = card.ENABLED ? 0 : 1;
    restApi.post(`/api/task/cards/${card.CARD_ID}/schedule`, {
      FREQUENCY: card.FREQUENCY,
      DAY_OF_WEEK: card.DAY_OF_WEEK,
      DAY_OF_MONTH: card.DAY_OF_MONTH,
      END_DAY_OF_WEEK: card.END_DAY_OF_WEEK,
      END_DAY_OF_MONTH: card.END_DAY_OF_MONTH,
      END_TIME: card.END_TIME,
      ENABLED: newEnabled,
    }, () => {
      loadRecurringCards();
    });
  };

  // 保存（新規追加）
  const handleSave = () => {
    const payload = {
      TITLE: newCard.TITLE,
      DESCRIPTION: newCard.DESCRIPTION,
      LIST_ID: newCard.LIST_ID,
      FREQUENCY: newCard.FREQUENCY,
      DAY_OF_WEEK: newCard.FREQUENCY === 'weekly' ? newCard.DAY_OF_WEEK : null,
      DAY_OF_MONTH: newCard.FREQUENCY === 'monthly' ? newCard.DAY_OF_MONTH : null,
      END_DAY_OF_WEEK: newCard.FREQUENCY === 'weekly' && newCard.END_DAY_OF_WEEK != null ? Number(newCard.END_DAY_OF_WEEK) : null,
      END_DAY_OF_MONTH: newCard.FREQUENCY === 'monthly' && newCard.END_DAY_OF_MONTH != null ? Number(newCard.END_DAY_OF_MONTH) : null,
      END_TIME: (newCard.END_DAY_OF_WEEK != null || newCard.END_DAY_OF_MONTH != null) ? newCard.END_TIME : null,
      checklist: newChecklist.map((it) => ({ WORK_CONTENT: it.text })),
    };

    restApi.post('/api/task/recurring-cards/add', payload, () => {
      loadRecurringCards();
      setAddMode(false);
      if (props.loadCards) props.loadCards();
    });
  };

  // 入力変更
  const handleChange = (prop) => (e) => {
    const value = e?.target?.value ?? e?.value ?? e;
    const updated = { ...newCard, [prop]: value };

    if (prop === 'FREQUENCY') {
      updated.END_DAY_OF_WEEK = null;
      updated.END_DAY_OF_MONTH = null;
      updated.END_TIME = '';
    }
    if (prop === 'END_DAY_OF_WEEK' && (value === '' || value == null)) {
      updated.END_TIME = '';
    }
    if (prop === 'END_DAY_OF_MONTH' && (value === '' || value == null)) {
      updated.END_TIME = '';
    }

    setNewCard(updated);
  };

  // チェックリスト操作
  const handleAddChecklistItem = () => {
    const text = newItemText.trim();
    if (!text) return;
    setNewChecklist([...newChecklist, { id: `${Date.now()}`, text }]);
    setNewItemText('');
  };

  const handleDeleteChecklistItem = (id) => {
    setNewChecklist(newChecklist.filter((it) => it.id !== id));
  };

  // 頻度のラベル
  const getFrequencyLabel = (card) => {
    if (card.FREQUENCY === 'weekly') {
      let label = `毎週${WEEKDAYS[card.DAY_OF_WEEK] || ''}曜`;
      if (card.END_DAY_OF_WEEK != null) {
        label += ` → ${WEEKDAYS[card.END_DAY_OF_WEEK]}曜`;
        if (card.END_TIME) label += ` ${card.END_TIME}`;
      }
      return label;
    }
    let label = `毎月${card.DAY_OF_MONTH || ''}日`;
    if (card.END_DAY_OF_MONTH != null) {
      label += ` → ${card.END_DAY_OF_MONTH}日`;
      if (card.END_TIME) label += ` ${card.END_TIME}`;
    }
    return label;
  };

  return (
    <>
      <Dialog
        sx={{
          '& .MuiDialog-container': {
            alignItems: 'flex-start',
            '& .MuiPaper-root': {
              width: '100%',
              maxWidth: '800px',
              backgroundColor: '#f1f2f4',
              marginTop: '40px',
            },
          },
        }}
        open={show}
        onClose={handleClose}
      >
        <DialogTitle className="p-3" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <AutorenewIcon style={{ color: '#1976d2' }} />
          定期タスク管理
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent className="px-3 pb-3">
          {addMode ? (
            // 追加フォーム
            <div style={{ backgroundColor: '#fff', borderRadius: '8px', padding: '16px' }}>
              <h6>定期タスク付きカード追加</h6>

              <Form.Group className="mb-2">
                <Form.Label style={{ fontWeight: 'bold', fontSize: '10pt' }}>タスク名</Form.Label>
                <Form.Control
                  type="text"
                  value={newCard.TITLE}
                  onChange={handleChange('TITLE')}
                  placeholder="カードのタイトル"
                  autoFocus
                />
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label style={{ fontWeight: 'bold', fontSize: '10pt' }}>説明</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={newCard.DESCRIPTION}
                  onChange={handleChange('DESCRIPTION')}
                  placeholder="説明（任意）"
                />
              </Form.Group>

              <Row className="mb-2">
                <Col md={4}>
                  <Form.Label style={{ fontWeight: 'bold', fontSize: '10pt' }}>配置先リスト</Form.Label>
                  <SelectComponent
                    value={newCard.LIST_ID}
                    onChange={handleChange('LIST_ID')}
                  >
                    {lists.map((l) => (
                      <option key={l.LIST_ID} value={l.LIST_ID}>
                        {l.LIST_NAME}
                      </option>
                    ))}
                  </SelectComponent>
                </Col>
                <Col md={3}>
                  <Form.Label style={{ fontWeight: 'bold', fontSize: '10pt' }}>頻度</Form.Label>
                  <SelectComponent
                    value={newCard.FREQUENCY}
                    onChange={handleChange('FREQUENCY')}
                  >
                    <option value="weekly">毎週</option>
                    <option value="monthly">毎月</option>
                  </SelectComponent>
                </Col>
                <Col md={3}>
                  {newCard.FREQUENCY === 'weekly' ? (
                    <>
                      <Form.Label style={{ fontWeight: 'bold', fontSize: '10pt' }}>開始曜日</Form.Label>
                      <SelectComponent
                        value={newCard.DAY_OF_WEEK}
                        onChange={handleChange('DAY_OF_WEEK')}
                      >
                        {WEEKDAYS.map((d, i) => (
                          <option key={i} value={i}>
                            {d}曜日
                          </option>
                        ))}
                      </SelectComponent>
                    </>
                  ) : (
                    <>
                      <Form.Label style={{ fontWeight: 'bold', fontSize: '10pt' }}>開始日</Form.Label>
                      <SelectComponent
                        value={newCard.DAY_OF_MONTH}
                        onChange={handleChange('DAY_OF_MONTH')}
                      >
                        {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                          <option key={d} value={d}>
                            {d}日
                          </option>
                        ))}
                      </SelectComponent>
                    </>
                  )}
                </Col>
                <Col md={3}>
                  {newCard.FREQUENCY === 'weekly' ? (
                    <>
                      <Form.Label style={{ fontWeight: 'bold', fontSize: '10pt' }}>終了曜日</Form.Label>
                      <SelectComponent
                        value={newCard.END_DAY_OF_WEEK ?? ''}
                        onChange={handleChange('END_DAY_OF_WEEK')}
                      >
                        <option value="">なし</option>
                        {WEEKDAYS.map((d, i) => (
                          <option key={i} value={i}>
                            {d}曜日
                          </option>
                        ))}
                      </SelectComponent>
                    </>
                  ) : (
                    <>
                      <Form.Label style={{ fontWeight: 'bold', fontSize: '10pt' }}>終了日</Form.Label>
                      <SelectComponent
                        value={newCard.END_DAY_OF_MONTH ?? ''}
                        onChange={handleChange('END_DAY_OF_MONTH')}
                      >
                        <option value="">なし</option>
                        {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                          <option key={d} value={d}>
                            {d}日
                          </option>
                        ))}
                      </SelectComponent>
                    </>
                  )}
                </Col>
                {(newCard.END_DAY_OF_WEEK != null && newCard.END_DAY_OF_WEEK !== '' ||
                  newCard.END_DAY_OF_MONTH != null && newCard.END_DAY_OF_MONTH !== '') && (
                  <Col md={2}>
                    <Form.Label style={{ fontWeight: 'bold', fontSize: '10pt' }}>終了時刻</Form.Label>
                    <SelectComponent
                      value={newCard.END_TIME || ''}
                      onChange={handleChange('END_TIME')}
                    >
                      <option value="">なし</option>
                      {Array.from({ length: 8 }, (_, i) => i + 10)
                        .map((i) => `${i.toString().padStart(2, '0')}:00`)
                        .map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                    </SelectComponent>
                  </Col>
                )}
              </Row>

              {/* チェックリスト */}
              <Form.Label style={{ fontWeight: 'bold', fontSize: '10pt' }}>チェックリスト</Form.Label>
              <List dense sx={{ m: 0, p: 0 }}>
                {newChecklist.map((it) => (
                  <ListItem
                    key={it.id}
                    sx={{ bgcolor: '#f5f5f5', borderRadius: 1, mb: 0.5, py: 0.5 }}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => handleDeleteChecklistItem(it.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    }
                  >
                    <span style={{ fontSize: '10pt' }}>{it.text}</span>
                  </ListItem>
                ))}
              </List>
              <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="項目を入力して Enter または 追加"
                  value={newItemText}
                  onChange={(e) => setNewItemText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                      e.preventDefault();
                      handleAddChecklistItem();
                    }
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#fff' } }}
                />
                <Button
                  size="sm"
                  variant="primary"
                  className="btn-fill"
                  disabled={!newItemText.trim()}
                  onClick={handleAddChecklistItem}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  追加
                </Button>
              </div>

              <div className="mt-3" style={{ display: 'flex', gap: '8px' }}>
                <Button
                  variant="primary"
                  className="btn-fill"
                  onClick={handleSave}
                  disabled={!newCard.TITLE?.trim()}
                >
                  保存
                </Button>
                <Button variant="secondary" onClick={() => setAddMode(false)}>
                  キャンセル
                </Button>
              </div>
            </div>
          ) : (
            // 一覧表示
            <>
              <Button
                variant="primary"
                className="btn-fill mb-3"
                size="sm"
                onClick={handleAddNew}
                disabled={lists.length === 0}
              >
                <AddIcon fontSize="small" />
                定期タスクを追加
              </Button>

              {recurringCards.length === 0 && (
                <div style={{ textAlign: 'center', color: '#888', padding: '20px' }}>
                  定期タスクがありません
                </div>
              )}

              {recurringCards.map((card) => (
                <div
                  key={card.CARD_ID}
                  style={{
                    backgroundColor: '#fff',
                    borderRadius: '8px',
                    padding: '12px 16px',
                    marginBottom: '8px',
                    borderLeft: `3px solid ${card.ENABLED ? '#4dabf7' : '#ccc'}`,
                    opacity: card.ENABLED ? 1 : 0.6,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Switch
                      size="small"
                      checked={!!card.ENABLED}
                      onChange={() => handleToggleEnabled(card)}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 'bold', fontSize: '11pt' }}>
                        {card.TITLE}
                      </div>
                      <div style={{ fontSize: '9pt', color: '#666', marginTop: '2px' }}>
                        <Chip
                          label={getFrequencyLabel(card)}
                          size="small"
                          sx={{ mr: 1, fontSize: '9pt', height: '20px' }}
                        />
                        → リスト: {card.INITIAL_LIST_NAME || '(不明)'}
                        {card.LIST_ID !== card.INITIAL_LIST_ID && '（完了）'}
                        {card.LAST_RESET_DATE && (
                          <span style={{ marginLeft: '12px', color: '#999' }}>
                            最終リセット: {card.LAST_RESET_DATE}
                          </span>
                        )}
                      </div>
                      {card.CHECKLIST_COUNT > 0 && (
                        <div style={{ fontSize: '9pt', color: '#888', marginTop: '4px' }}>
                          チェックリスト: {card.CHECKLIST_COUNT}件
                        </div>
                      )}
                    </div>
                    <IconButton size="small" onClick={() => handleEdit(card)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </div>
                </div>
              ))}
            </>
          )}
        </DialogContent>
      </Dialog>
      <MessageModal dialog={dialog}></MessageModal>
    </>
  );
}

export default RecurringTaskManageModal;
